import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  calendarRange,
  moveCalendarAnchor,
  type CalendarView,
  type WeekRange,
} from "./weekRange.js";

/**
 * Die angezeigte Woche steht in der Adresse, damit Sidebar und Inhalt dieselbe sehen —
 * und damit sich ein Link auf eine bestimmte Woche verschicken lässt.
 */
export interface CalendarLocation {
  view: CalendarView;
  anchor: Date;
  range: WeekRange;
  showView: (view: CalendarView) => void;
  showDate: (anchor: Date) => void;
  showDay: (anchor: Date) => void;
  move: (amount: number) => void;
  /** Kalender, die gerade ausgeblendet sind. */
  hidden: ReadonlySet<string>;
  toggleCalendar: (calendarId: string) => void;
  eventId: string | null;
  showEvent: (eventId: string | null) => void;
}

function parseAnchor(value: string | null): Date {
  if (value) {
    const parsed = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return new Date();
}

function toParam(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function useCalendarLocation(): CalendarLocation {
  const [params, setParams] = useSearchParams();
  const requestedView = params.get("view");
  const view: CalendarView = requestedView === "overview" || requestedView === "day" || requestedView === "month"
    ? requestedView
    : "week";
  const anchor = parseAnchor(params.get("date") ?? params.get("week"));

  const showDate = useCallback(
    (next: Date) => {
      setParams((current) => {
        const updated = new URLSearchParams(current);
        updated.set("date", toParam(next));
        updated.delete("week");
        updated.delete("panel");
        return updated;
      });
    },
    [setParams],
  );

  const showView = useCallback((nextView: CalendarView) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.set("view", nextView);
      if (!updated.has("date")) updated.set("date", toParam(anchor));
      updated.delete("week");
      updated.delete("panel");
      return updated;
    });
  }, [anchor, setParams]);

  const showDay = useCallback((next: Date) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.set("view", "day");
      updated.set("date", toParam(next));
      updated.delete("week");
      updated.delete("panel");
      return updated;
    });
  }, [setParams]);

  const move = useCallback((amount: number) => {
    showDate(moveCalendarAnchor(view, anchor, amount));
  }, [anchor, showDate, view]);

  const hiddenParam = params.get("hidden") ?? "";
  const eventId = params.get("event");
  const showEvent = useCallback((nextEventId: string | null) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      if (nextEventId) updated.set("event", nextEventId);
      else updated.delete("event");
      return updated;
    });
  }, [setParams]);
  const hidden = useMemo(
    () => new Set(hiddenParam.split(",").filter((entry) => entry.length > 0)),
    [hiddenParam],
  );

  const toggleCalendar = useCallback(
    (calendarId: string) => {
      setParams((current) => {
        const updated = new URLSearchParams(current);
        const next = new Set((updated.get("hidden") ?? "").split(",").filter(Boolean));
        if (next.has(calendarId)) {
          next.delete(calendarId);
        } else {
          next.add(calendarId);
        }
        if (next.size > 0) {
          updated.set("hidden", [...next].join(","));
        } else {
          updated.delete("hidden");
        }
        updated.delete("panel");
        return updated;
      });
    },
    [setParams],
  );

  const anchorTime = anchor.getTime();
  const range = useMemo(() => calendarRange(view, new Date(anchorTime)), [anchorTime, view]);

  return useMemo(
    () => ({ view, anchor, range, showView, showDate, showDay, move, hidden, toggleCalendar, eventId, showEvent }),
    [view, anchorTime, range, showView, showDate, showDay, move, hidden, toggleCalendar, eventId, showEvent],
  );
}
